import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import { LinearProgress, List, ListItem } from '@mui/material';
import { ProgressContainer } from './styles';
import { IStateProps, IDispatchProps, IProps } from './interfaces';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogContentContainer,
  DialogActionsOverride,
  ActionContainer,
} from '../../../../Shared/styles/dialog';
import { WarningContainer } from '../../../../Shared/Warning';
import Button from '../../../../Shared/VaultButton';
import { IAppState } from '../../../../../state/reducers';
import {
  clearAll,
  loadGameAssets,
  toggleDeleteGameDialog,
  toggleDeleteGameDialogLoading,
} from '../../../../../state/exchange/updateGameScheduleDialog';
import { deleteSchedule } from '../../../../../api/vault/exchangeSchedule';
import { deleteAssets } from '../../../../../api/vault/assets';
import toast, { superAdminToast } from '../../../../../utils/toast';
import { selectIsUserVaultSuperAdmin } from '../../../../../state/user/userOrgRole';

class DeleteGameForm extends PureComponent<IProps> {
  onDeleteSchedule = async () => {
    const {
      game: {
        _id: gameId,
        NCAAGameNumber,
      },
      assets,
      onSave,
      onToggleLoading,
      isVaultSuperAdmin,
    } = this.props;
    onToggleLoading();
    try {
      await deleteAssets({
        ids: assets.map(({ _id }) => _id),
      });
      superAdminToast(isVaultSuperAdmin, 'Assets associated with game have been purged successfully', { type: toast.TYPE.SUCCESS });
    } catch (e) {
      superAdminToast(isVaultSuperAdmin, 'Assets could not be deleted', { type: toast.TYPE.ERROR });
    }
    await deleteSchedule(gameId);
    superAdminToast(isVaultSuperAdmin, `Game "${NCAAGameNumber}" deleted successfully`, { type: toast.TYPE.SUCCESS });
    onSave();
  };

  componentDidMount() {
    const { onLoadAssets } = this.props;
    onLoadAssets();
  }

  render() {
    const {
      isLoading,
      isLoaded,
      assets,
      game,
      onExit,
    } = this.props;

    if (!game) {
      return (
        <h1>Error</h1>
      );
    }

    const { NCAAGameNumber } = game;

    return (
      <>
        <DialogHeader
          data-auto="DeleteScheduleGameDialog"
        >
          <DialogHeaderTitle>
            Delete Game
          </DialogHeaderTitle>
        </DialogHeader>
        <ProgressContainer>
          {isLoading && <LinearProgress />}
        </ProgressContainer>
        <DialogContentContainer>
          {(isLoaded && assets.length > 0) && (
            <>
              <WarningContainer>
                <h1>{assets.length} assets associated with game {NCAAGameNumber} will be purged upon game deletion</h1>
              </WarningContainer>
              <List>
                {assets.map(({ _source: { title, exchangeQualifier } }, index) => <ListItem key={index}>{title} | {exchangeQualifier}</ListItem>)}
              </List>
            </>
          )}
          {(isLoaded && assets.length === 0) && (
            <WarningContainer>
              <h1>No assets found associated with this game</h1>
            </WarningContainer>
          )}
          <DialogActionsOverride>
            <ActionContainer>
              <Button
                variant="plainOnPaper"
                onClick={onExit}
                data-auto="ConfirmCancelBtn"
              >
                Cancel
              </Button>
              <Button
                variant="primaryOnWhite"
                onClick={() => this.onDeleteSchedule()}
                data-auto="ConfirmDeleteBtn"
                disabled={!isLoaded || isLoading}
              >
                Continue
              </Button>
            </ActionContainer>
          </DialogActionsOverride>
        </DialogContentContainer>
      </>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  game: state.exchange?.gameScheduleForm?.selectedSchedule,
  isLoading: state.exchange?.updateGameScheduleDialog?.deleteDialog?.isLoading ?? false,
  isLoaded: state.exchange?.updateGameScheduleDialog?.deleteDialog?.isLoaded ?? false,
  assets: state.exchange?.updateGameScheduleDialog?.deleteDialog?.assets ?? [],
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
});

const mapDispatchToProps: IDispatchProps = {
  onLoadAssets: loadGameAssets,
  onToggleLoading: toggleDeleteGameDialogLoading,
  onExit: toggleDeleteGameDialog,
  onSave: clearAll,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DeleteGameForm);
